import React, { memo, useMemo, useRef, useState } from 'react'
import { Client, HTLChannels, HTLObjective, PickByType } from '@types'
import { InfoTooltip } from '@components/InfoTooltip'
import FileUploadInput from '@components/FileUploadInput'
import {
  AspectRatio,
  Button,
  FormControl,
  Grid,
  FormHelperText,
  Typography,
  Stack,
  Switch,
  ModalDialog,
  ModalClose,
  Modal,
  Tooltip,
  IconButton,
  Box,
} from '@mui/joy'
import Stepper, { Step, StepperAccordion, StepperAccordionSection } from '@components/Stepper'
import { FloatingLabelInput } from '@components/FloatingLabelInput'
import { useClient } from '@hooks/useClient'
import { useSession } from '@hooks/useSession'
import {
  DateInput,
  MultiSelectWithChipsInput,
  OptionArrayBox,
  SearchableSelectInput,
  SelectInput,
  TextAreaInput,
  TextInput,
} from './Inputs'
import {
  excludeFunctionsFromMemo,
  exclusivePick,
  isForbidden,
  isValidSections,
  isValidSteps,
  sizeToBytes,
} from '@utils'
import { FileGridPro } from '@components/Grids'
// import { EventsCard } from '@components/EventsCard'
import { TabContent } from './Activity'
// import { useClientEvents } from '@hooks/useClientEvents'
import { ContentCopy, OpenInNew } from '@mui/icons-material'
import { HTL_LEVEL, MKTG_MODES, MKTG_PROFILES } from '@constants'
import { colors, globalStyles } from '@styles/styles'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

interface Props {
  id?: number
  onClose?: () => void
}

const gridContainerProps = {
  container: true,
  spacing: 3,
}

const gridItemProps = {
  xs: 12,
  md: 6,
}

const htlStyles = {
  emptyState: {
    border: globalStyles.dashedBorder,
    borderRadius: globalStyles.borderRadius,
    py: 4,
    px: 3,
    textAlign: 'center',
  },
  objectiveCard: {
    borderRadius: globalStyles.borderRadius,
    border: globalStyles.border,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  gestionCard: {
    borderRadius: globalStyles.borderRadius,
    border: globalStyles.border,
    bgcolor: colors.darkWhite,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  addGestionButton: {
    alignSelf: { xs: 'stretch', sm: 'flex-start' },
  },
  addObjectiveButton: {
    alignSelf: { xs: 'stretch', sm: 'flex-start' },
  },
}

export default function ClientForm({ id, onClose }: Props) {
  const { userLevel } = useSession()
  const [showSizeAlertModal, setShowSizeAlertModal] = useState(false)
  const [crossedSteps, setCrossedSteps] = useState<number[]>([])
  const [uidCopyTooltipOpen, setUidCopyTooltipOpen] = useState(false)
  const uidCopyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const onThroughStep = (step: number) => {
    if (crossedSteps.includes(step)) return
    setCrossedSteps([...crossedSteps, step])
  }

  const {
    client,
    isLoadingClient,
    isErrorClient,
    validationErrors,
    saveClient,
    saveMarketingObjectives,
    handleModuleSelection,
    handleLogoInput,
    handleModuleNotification,
    handleButtonLabel,
    setValue,
    backendValidationErrors,
    setPhoneValue,
    setDomain,
    isSaving,
    errorClient,
    refetchClient,
    queriedModules,
    addFiles,
    removeFile,
    htlObjectives,
    addObjective,
    removeObjective,
    handleObjectiveFieldChange,
    addGestion,
    removeGestion,
    handleGestionFieldChange,
    hasMarketingModule,
    lastSiteDeploy,
  } = useClient({
    id,
  })

  // const {
  //   allClientEvents,
  //   deleteClientEvent,
  //   updateClientEvent,
  //   isBusyClientEvents,
  //   isDeletingClientEvent,
  //   isUpdatingClientEvent,
  //   clientEvent,
  //   setEventValue,
  //   isSavingEvent,
  //   saveClientEvent,
  // } = useClientEvents({ id })

  const clientGeolocation = [
    'CABA',
    'Zona Norte',
    'Zona Oeste',
    'Zona Sur',
    'Buenos Aires',
    'Catamarca',
    'Chaco',
    'Chubut',
    'Córdoba',
    'Corrientes',
    'Entre Ríos',
    'Formosa',
    'Jujuy',
    'La Pampa',
    'La Rioja',
    'Mendoza',
    'Misiones',
    'Neuquén',
    'Río Negro',
    'Salta',
    'San Juan',
    'San Luis',
    'Santa Cruz',
    'Santa Fe',
    'Santiago del Estero',
    'Tierra del Fuego',
    'Tucumán',
  ]

  const VATCondition = [
    'IVA Responsable Inscripto',
    'IVA Responsable no Inscripto',
    'IVA Sujeto Exento',
    'Consumidor Final',
    'Responsable Monotributo',
  ]

  const clientCategory = ['Inicial', 'Profesional', 'Corporativo']

  const clientStatus = ['Prospecto', 'Cliente']

  const clientTags = ['Bonificado']

  const disableDomainEdit = Boolean(client.is_domain_disabled)

  const domainUrl = useMemo(() => {
    const domain = client?.domains?.[0]?.name?.trim()
    if (!domain) return undefined
    if (/^https?:\/\//i.test(domain)) return domain
    return `https://${domain}`
  }, [client?.domains?.[0]?.name])
  const lastDeployLink = lastSiteDeploy?.deploy_ssl_url
  const lastDeployDate = lastSiteDeploy?.created_at ? new Date(lastSiteDeploy.created_at).toLocaleString() : undefined

  const sizeAlertModal = (
    <Modal
      open={showSizeAlertModal}
      onClose={() => setShowSizeAlertModal(false)}
    >
      <ModalDialog
        sx={{
          maxWidth: 500,
          p: 3,
        }}
      >
        <ModalClose />
        <Stack gap={3}>
          <Typography level="body-md-bold">
            El archivo no pudo ser cargado. Recuerde que los archivos deben cumplir con los requerimientos: <br></br>
            Formatos: *.pdf *.jpg, *.docx, *.pptx, *.xlsx.
            <br></br>
            Peso máximo por archivo: 2MB
          </Typography>
          <Button onClick={() => setShowSizeAlertModal(false)}>Cerrar</Button>
        </Stack>
      </ModalDialog>
    </Modal>
  )

  const gridSwitch = ({ label, name, disabled }: { label: string; name: keyof Client; disabled?: boolean }) => (
    <GridSwitch
      label={label}
      name={name}
      disabled={disabled}
      checked={client[name] as boolean}
      setValue={(name, value) => setValue(name as keyof PickByType<Client, boolean>, value)}
    />
  )

  const handleCopyUid = async () => {
    if (!client?.uid) return
    await navigator.clipboard.writeText(client.uid)
    setUidCopyTooltipOpen(true)

    if (uidCopyTimeoutRef.current) {
      clearTimeout(uidCopyTimeoutRef.current)
    }

    uidCopyTimeoutRef.current = setTimeout(() => {
      setUidCopyTooltipOpen(false)
      uidCopyTimeoutRef.current = null
    }, 2000)
  }

  const filesDataValidationError = (order: number) => {
    const index = client.files_data?.findIndex((fd) => fd.order === order)
    const indexError = validationErrors?.[`files_data[${index}].label`]

    return indexError
  }

  const htlLevelOptions = useMemo(() => (HTL_LEVEL ?? []).map((level) => ({ value: level, label: level })), [])

  const modeOptions = useMemo(() => Object.keys(MKTG_MODES).map((mode) => ({ value: mode, label: mode })), [])

  const parseNumberValue = (value: string) => {
    if (value.trim() === '') return undefined
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  const sections: StepperAccordionSection[] = [
    {
      title: 'Datos generales',
      items: [
        {
          title: 'Datos Legales y Fiscales',
          content: (
            <>
              <Grid {...gridContainerProps}>
                {[
                  { label: 'Razón social', name: 'legal_name' as const, value: client?.legal_name },
                  { label: 'CUIT (sin guiones)', name: 'cuit' as const, value: client?.cuit },
                  { label: 'Dirección fiscal', name: 'fiscal_address' as const, value: client?.fiscal_address },
                  { label: 'IIBB', name: 'income_tax' as const, value: client?.income_tax },
                ].map(({ label, name, value }) => (
                  <Grid
                    key={name}
                    {...gridItemProps}
                  >
                    <TextInput<Client>
                      label={label}
                      name={name}
                      value={value}
                      onChange={setValue}
                    />
                  </Grid>
                ))}
                <Grid {...gridItemProps}>
                  <SelectInput<Client, string>
                    label="Condición de IVA"
                    name="VAT_condition"
                    options={VATCondition}
                    value={client?.VAT_condition}
                    onChange={setValue}
                  />
                </Grid>
                <Grid {...gridItemProps}>
                  <OptionArrayBox
                    label="Agente de retención"
                    sx={{ padding: 1 }}
                  >
                    {gridSwitch({
                      label: 'Si',
                      name: 'withholding_agent',
                    })}
                  </OptionArrayBox>
                </Grid>
                <DateInput<Client>
                  name="date_registration"
                  label="Fecha de alta"
                  value={client?.date_registration}
                  onChange={setValue<string>}
                  gridProps={{ xs: 12, md: 6 }}
                />
                <Grid {...gridItemProps}>
                  <MultiSelectWithChipsInput<{ tags: string[] }>
                    name="tags"
                    label="Etiquetas"
                    value={client.tags ?? []}
                    options={clientTags}
                    onChange={(field, newValue) => {
                      const onlyValues = newValue.map((opt) => opt)
                      setValue(field as 'speciality', onlyValues)
                    }}
                  />
                </Grid>
                <Grid
                  {...gridItemProps}
                  md={12}
                >
                  <TextAreaInput<Client>
                    label="Detalle"
                    name="detail"
                    value={client?.detail}
                    onChange={setValue}
                    minRows={6}
                  />
                </Grid>
              </Grid>
            </>
          ),
        },
        {
          title: 'Perfil del Cliente y Objetivos',
          errorFields: Object.values(exclusivePick(validationErrors, 'name', 'status')),
          content: (
            <>
              <Grid {...gridContainerProps}>
                <Grid {...gridItemProps}>
                  <TextInput<Client>
                    label="Nombre de fantasía"
                    name="name"
                    error={'name' in validationErrors}
                    errorMessage={validationErrors?.name}
                    value={client?.name}
                    onChange={setValue}
                  />
                </Grid>
                <SearchableSelectInput<Client, string>
                  value={client?.profile}
                  onChange={setValue}
                  options={Object.keys(MKTG_PROFILES).map((p) => ({ value: p, label: p }))}
                  searchPlaceholder="Seleccionar perfil de empresa"
                  noResultsText="No se encontraron resultados"
                  name="profile"
                  label="Perfil"
                  gridProps={{ xs: 12, md: 6 }}
                />
                <SearchableSelectInput<Client, string>
                  value={client?.speciality}
                  onChange={setValue}
                  options={(MKTG_PROFILES[client?.profile ?? ''] ?? []).map((seg) => ({
                    value: seg,
                    label: seg,
                  }))}
                  searchPlaceholder={'Seleccionar subsegmento'}
                  noResultsText="No se encontraron resultados"
                  name="speciality"
                  label="Subsegmento"
                  gridProps={{ xs: 12, md: 6 }}
                  multiple
                />
                <Grid {...gridItemProps}>
                  <SelectInput<Client, string>
                    label="Ubicación geográfica de operaciones"
                    name="geolocation"
                    options={clientGeolocation}
                    value={client?.geolocation}
                    onChange={setValue}
                  />
                </Grid>
                <Grid {...gridItemProps}>
                  <SelectInput<Client, string>
                    label="Categoría"
                    name="category"
                    options={clientCategory}
                    value={client?.category}
                    onChange={setValue}
                  />
                </Grid>
                <Grid {...gridItemProps}>
                  <SelectInput<Client, string>
                    label="Estado comercial"
                    name="status"
                    options={clientStatus}
                    value={client?.status}
                    onChange={setValue}
                    error={'status' in validationErrors}
                    errorMessage={validationErrors?.status}
                  />
                </Grid>
              </Grid>
            </>
          ),
        },
        {
          title: 'Datos de Contacto y Canales de Comunicación',
          errorFields: Object.values(exclusivePick(validationErrors, 'email', 'domain')),
          content: (
            <>
              <Grid {...gridContainerProps}>
                <Grid {...gridItemProps}>
                  <TextInput<Client>
                    label="Email"
                    name="email"
                    error={'email' in validationErrors}
                    errorMessage={validationErrors?.email}
                    value={client?.email}
                    onChange={setValue}
                  />
                </Grid>
                <Grid {...gridItemProps}>
                  <TextInput<Client['phones']>
                    label="Teléfono"
                    name="primaryPhone"
                    error={'primaryPhone' in validationErrors}
                    errorMessage={validationErrors?.primaryPhone}
                    value={client?.phones?.primaryPhone}
                    onChange={setPhoneValue}
                  />
                </Grid>
                <Grid {...gridItemProps}>
                  <TextInput<Client['phones']>
                    label="WhatsApp"
                    name="whatsapp"
                    value={client?.phones?.whatsapp}
                    onChange={setPhoneValue}
                  />
                </Grid>
                <Grid {...gridItemProps}>
                  <TextInput<Client>
                    label="Dominio web"
                    name={'domains' as any} // Forcing TextInput, only for legibility purposes
                    readOnly={disableDomainEdit}
                    error={'domain' in validationErrors || 'domain' in backendValidationErrors}
                    errorMessage={
                      disableDomainEdit
                        ? 'La edición del dominio se encuentra deshabilitada porque ya posee un sitio web publicado'
                        : validationErrors?.domain ?? backendValidationErrors?.domain
                    }
                    value={client?.domains?.[0]?.name}
                    onChange={(_, value) => setDomain(value)}
                    endDecorator={
                      <Button
                        component="a"
                        href={domainUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        variant="soft"
                        endDecorator={<OpenInNew sx={{ fontSize: '1em' }} />}
                      >
                        Abrir
                      </Button>
                    }
                  />
                  {lastDeployLink && lastSiteDeploy.state === 'ready' && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{ mt: 0.5 }}
                    >
                      <Tooltip title={lastDeployLink}>
                        <Button
                          component="a"
                          href={lastDeployLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          variant="soft"
                          endDecorator={<OpenInNew sx={{ fontSize: '1em' }} />}
                        >
                          Último deploy Netlify
                        </Button>
                      </Tooltip>
                      {lastDeployDate && <FormHelperText sx={{ m: 0 }}>{lastDeployDate}</FormHelperText>}
                    </Stack>
                  )}
                </Grid>
                <Grid {...gridItemProps}>
                  <OptionArrayBox
                    label="Tokko Broker"
                    sx={{ padding: 2 }}
                  >
                    {gridSwitch({
                      label: 'Usar TokkoBroker',
                      name: 'use_tokko',
                    })}
                    {Boolean(client?.use_tokko) && (
                      <TextInput<Client>
                        label="API Key de Tokko Broker"
                        name="tokko_api_key"
                        value={client?.tokko_api_key}
                        onChange={setValue}
                        gridProps={{ sx: { mt: 2, p: 0 } }}
                      />
                    )}
                  </OptionArrayBox>
                </Grid>
                <Grid {...gridItemProps}>
                  <OptionArrayBox
                    label="Pulppo"
                    sx={{ padding: 2 }}
                  >
                    {gridSwitch({
                      label: 'Usar Pulppo',
                      name: 'use_pulppo',
                    })}
                    {Boolean(client?.use_pulppo) && (
                      <TextInput<Client>
                        label="API Key de Pulppo"
                        name="pulppo_api_key"
                        value={client?.pulppo_api_key}
                        onChange={setValue}
                        gridProps={{ sx: { mt: 2, p: 0 } }}
                      />
                    )}
                  </OptionArrayBox>
                </Grid>
                <Grid {...gridItemProps}>
                  <OptionArrayBox
                    label="Mail Up"
                    sx={{ padding: 2 }}
                  >
                    {gridSwitch({
                      label: 'Usar Mail Up',
                      name: 'use_mailup',
                    })}
                    {Boolean(client?.use_mailup) && (
                      <>
                        <TextInput<NonNullable<Client['mailup']>>
                          label="Username"
                          name="username"
                          value={client?.mailup?.username || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('mailup', {
                              ...(client?.mailup ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['mailup']>>
                          label="Password"
                          name="password"
                          value={client?.mailup?.password || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('mailup', {
                              ...(client?.mailup ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['mailup']>>
                          label="client_id"
                          name="client_id"
                          value={client?.mailup?.client_id || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('mailup', {
                              ...(client?.mailup ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['mailup']>>
                          label="client_secret"
                          name="client_secret"
                          value={client?.mailup?.client_secret || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('mailup', {
                              ...(client?.mailup ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['mailup']>>
                          label="list_id"
                          name="list_id"
                          value={client?.mailup?.list_id || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('mailup', {
                              ...(client?.mailup ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                      </>
                    )}
                  </OptionArrayBox>
                </Grid>
                <Grid {...gridItemProps}>
                  <OptionArrayBox
                    label="Xintel"
                    sx={{ padding: 2 }}
                  >
                    {gridSwitch({
                      label: 'Usar Xintel',
                      name: 'use_xintel',
                    })}
                    {Boolean(client?.use_xintel) && (
                      <>
                        <TextInput<NonNullable<Client['xintel_keys']>>
                          label="Inm"
                          name="inm"
                          value={client?.xintel_keys?.inm || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('xintel_keys', {
                              ...(client?.xintel_keys ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['xintel_keys']>>
                          label="Api Key"
                          name="api_key"
                          value={client?.xintel_keys?.api_key || ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('xintel_keys', {
                              ...(client?.xintel_keys ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                      </>
                    )}
                  </OptionArrayBox>
                </Grid>
                <Grid {...gridItemProps}>
                  <OptionArrayBox
                    label="Metricool"
                    sx={{ padding: 2 }}
                  >
                    {gridSwitch({
                      label: 'Usar Metricool',
                      name: 'use_metricool',
                    })}
                    {Boolean(client?.use_metricool) && (
                      <>
                        <TextInput<NonNullable<Client['metricool']>>
                          label="API key"
                          name="api_key"
                          value={client?.metricool?.api_key ?? ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('metricool', {
                              ...(client?.metricool ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['metricool']>>
                          label="userId"
                          name="userId"
                          value={client?.metricool?.userId ?? ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('metricool', {
                              ...(client?.metricool ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                        <TextInput<NonNullable<Client['metricool']>>
                          label="blogId"
                          name="blogId"
                          value={client?.metricool?.blogId ?? ''}
                          onChange={(fieldName, fieldValue) =>
                            setValue('metricool', {
                              ...(client?.metricool ?? {}),
                              [fieldName]: fieldValue,
                            })
                          }
                          gridProps={{ sx: { mt: 2, p: 0 } }}
                        />
                      </>
                    )}
                  </OptionArrayBox>
                </Grid>
              </Grid>
            </>
          ),
        },
        {
          title: 'Identidad Visual y Recursos de Marca',
          errorFields: Object.values(exclusivePick(validationErrors, 'logo')),
          content: (
            <>
              <Grid {...gridContainerProps}>
                {' '}
                <Grid
                  {...gridItemProps}
                  md={12}
                >
                  <OptionArrayBox
                    label={
                      <Typography
                        color="neutral"
                        level="body-sm-light"
                        endDecorator={
                          <InfoTooltip
                            text={`Especificaciones técnicas:
                Formato de la imagen: *.jpg, *.png (recomendado)
                Dimensiones de la imagen: 640x360 px
                Proporción de aspecto: 16:9
                Modo color: RGB
                Peso máximo recomendado: 800kB`}
                          />
                        }
                      >
                        Subir logo
                      </Typography>
                    }
                    error={'logo' in validationErrors}
                    errorMessage={validationErrors?.logo}
                  >
                    <Grid xs={12}>
                      <FileUploadInput
                        onChange={handleLogoInput}
                        acceptedTypes="image/*"
                        maxFileSize={sizeToBytes(0.8, 'MB')}
                      />
                      {Boolean(client?.logo) && (
                        <AspectRatio objectFit="contain">
                          <img
                            src={
                              typeof client?.logo === 'string'
                                ? client?.logo
                                : client?.logo instanceof File
                                ? URL.createObjectURL(client?.logo)
                                : ''
                            }
                          />
                        </AspectRatio>
                      )}
                    </Grid>
                  </OptionArrayBox>
                </Grid>
              </Grid>
            </>
          ),
        },
      ],
    },
    {
      title: 'Carga HTL',
      hidden: !hasMarketingModule,
      items: [
        {
          title: 'Objetivos comerciales HTL',
          content: (
            <Grid {...gridContainerProps}>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <OptionArrayBox
                  label="Objetivos comerciales HTL"
                  sx={{ padding: 2 }}
                >
                  <Stack gap={3}>
                    {htlObjectives.length === 0 ? (
                      <Stack
                        gap={2}
                        alignItems="center"
                        justifyContent="center"
                        sx={htlStyles.emptyState}
                      >
                        <Typography level="body-sm">Todavía no se cargaron objetivos HTL.</Typography>
                        <Button
                          size="sm"
                          variant="outlined"
                          startDecorator={<PlusIcon width="1em" />}
                          onClick={addObjective}
                        >
                          Agregar objetivo
                        </Button>
                      </Stack>
                    ) : (
                      htlObjectives.map((objective, objectiveIndex) => (
                        <Box
                          key={`htl-objective-${objectiveIndex}`}
                          sx={htlStyles.objectiveCard}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography level="body-md-bold">{`Objetivo ${objectiveIndex + 1}`}</Typography>
                            <Tooltip title="Eliminar objetivo">
                              <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={() => removeObjective(objectiveIndex)}
                              >
                                <TrashIcon width={18} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Grid
                            container
                            spacing={2}
                          >
                            <SearchableSelectInput<HTLObjective, string>
                              name="htl_level"
                              label="Objetivo comercial"
                              value={objective.htl_level}
                              options={htlLevelOptions}
                              onChange={(_, newValue) =>
                                handleObjectiveFieldChange(
                                  objectiveIndex,
                                  'htl_level',
                                  (newValue ?? undefined) as HTLObjective['htl_level'],
                                )
                              }
                              gridProps={{ xs: 12, md: 6 }}
                              searchPlaceholder="Seleccionar objetivo"
                            />
                            <TextInput<HTLObjective>
                              name="title"
                              label="Título"
                              value={objective.title ?? ''}
                              onChange={(_, newValue) => handleObjectiveFieldChange(objectiveIndex, 'title', newValue)}
                              gridProps={{ xs: 12, md: 6 }}
                            />
                            <Grid
                              xs={12}
                              md={6}
                            >
                              <FormControl>
                                <FloatingLabelInput
                                  label="KPI"
                                  type="number"
                                  value={objective.kpi !== undefined ? String(objective.kpi) : ''}
                                  onChange={(event) =>
                                    handleObjectiveFieldChange(
                                      objectiveIndex,
                                      'kpi',
                                      parseNumberValue(event.target.value),
                                    )
                                  }
                                  min={0}
                                />
                              </FormControl>
                            </Grid>
                            <Grid
                              xs={12}
                              md={6}
                            >
                              <FormControl>
                                <FloatingLabelInput
                                  label="Duración total"
                                  type="number"
                                  value={objective.total_duration !== undefined ? String(objective.total_duration) : ''}
                                  onChange={(event) =>
                                    handleObjectiveFieldChange(
                                      objectiveIndex,
                                      'total_duration',
                                      parseNumberValue(event.target.value),
                                    )
                                  }
                                  min={0}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                          <Stack gap={2}>
                            <Typography level="body-sm-bold">Gestiones</Typography>
                            {objective.channels && objective.channels.length > 0 ? (
                              objective.channels.map((channel, gestionIndex) => (
                                <Box
                                  key={`htl-gestion-${`${objectiveIndex}-${gestionIndex}`}`}
                                  sx={htlStyles.gestionCard}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Typography level="body-sm-bold">{`Gestión ${gestionIndex + 1}`}</Typography>
                                    <Tooltip title="Eliminar gestión">
                                      <IconButton
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        onClick={() => removeGestion(objectiveIndex, gestionIndex)}
                                      >
                                        <TrashIcon width={16} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                  <Grid
                                    container
                                    spacing={1}
                                  >
                                    <SearchableSelectInput<HTLChannels, string>
                                      name="mode"
                                      label="Gestión"
                                      value={channel.mode}
                                      options={modeOptions}
                                      onChange={(_, newValue) =>
                                        handleGestionFieldChange(
                                          objectiveIndex,
                                          gestionIndex,
                                          'mode',
                                          (newValue ?? undefined) as HTLChannels['mode'],
                                        )
                                      }
                                      gridProps={{ xs: 12, md: 4 }}
                                      searchPlaceholder="Seleccionar gestión"
                                    />
                                    <TextInput<HTLChannels>
                                      name="title"
                                      label="Título"
                                      value={channel.title ?? ''}
                                      onChange={(_, newValue) =>
                                        handleGestionFieldChange(objectiveIndex, gestionIndex, 'title', newValue)
                                      }
                                      gridProps={{ xs: 12, md: 4 }}
                                    />
                                    <Grid
                                      xs={12}
                                      md={4}
                                    >
                                      <FormControl>
                                        <FloatingLabelInput
                                          label="KPI"
                                          type="number"
                                          value={channel.kpi !== undefined ? String(channel.kpi) : ''}
                                          onChange={(event) =>
                                            handleGestionFieldChange(
                                              objectiveIndex,
                                              gestionIndex,
                                              'kpi',
                                              parseNumberValue(event.target.value),
                                            )
                                          }
                                          min={0}
                                        />
                                      </FormControl>
                                    </Grid>
                                  </Grid>
                                </Box>
                              ))
                            ) : (
                              <Typography level="body-sm">
                                Todavía no se cargaron gestiones para este objetivo.
                              </Typography>
                            )}
                            <Button
                              size="sm"
                              variant="outlined"
                              startDecorator={<PlusIcon width="1em" />}
                              onClick={() => addGestion(objectiveIndex)}
                              sx={htlStyles.addGestionButton}
                            >
                              Agregar gestión
                            </Button>
                          </Stack>
                        </Box>
                      ))
                    )}
                    {htlObjectives.length > 0 && (
                      <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<PlusIcon width="1em" />}
                        onClick={addObjective}
                        sx={htlStyles.addObjectiveButton}
                      >
                        Agregar objetivo
                      </Button>
                    )}
                  </Stack>
                </OptionArrayBox>
              </Grid>
            </Grid>
          ),
        },
      ],
    },
    {
      title: 'Documentación Comercial',
      items: [
        {
          title: 'Presupuesto tentativo',
          content: (
            <Grid
              {...gridItemProps}
              md={12}
            >
              <OptionArrayBox
                label="Presupuesto"
                sx={{ padding: 2 }}
                labelProps={{
                  endDecorator: (
                    <InfoTooltip
                      className="lh-base ps-2"
                      text={`Especificaciones técnicas:
                Formato: *.pdf *.jpg, *.docx, *.pptx, *.xlsx
                Peso máximo recomendado: 2MB`}
                    />
                  ),
                }}
              >
                <Stack gap={2}>
                  <FileUploadInput
                    onChange={(files) => addFiles(files, 1)}
                    acceptedTypes="application/pdf, image/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                  <FileGridPro
                    files={client?.files_data?.filter((fd) => fd.order === 1)}
                    onDelete={() => removeFile(1)}
                    showDownload
                  />
                  <FormControl error={Boolean(filesDataValidationError(1))}>
                    <FloatingLabelInput
                      label="Nombre de la pestaña"
                      value={client?.files_data?.find((fd) => fd.order === 1)?.label ?? ''}
                      onChange={(e) => handleButtonLabel(1, e.target.value)}
                      slotProps={{
                        input: {
                          maxLength: 25,
                        },
                      }}
                    />
                    <FormHelperText>{filesDataValidationError(1) ?? ''}</FormHelperText>
                  </FormControl>
                </Stack>
              </OptionArrayBox>
            </Grid>
          ),
          errorFields: [filesDataValidationError(1), filesDataValidationError(2)].filter(Boolean),
        },
        {
          title: 'Propuesta comercial',
          content: (
            <Grid
              {...gridItemProps}
              md={12}
            >
              <OptionArrayBox
                label="Propuesta comercial"
                sx={{ padding: 2 }}
                labelProps={{
                  endDecorator: (
                    <InfoTooltip
                      className="lh-base ps-2"
                      text={`Especificaciones técnicas:
                Formato: *.pdf *.jpg, *.docx, *.pptx, *.xlsx
                Peso máximo recomendado: 2MB`}
                    />
                  ),
                }}
                {...gridItemProps}
              >
                <Stack gap={2}>
                  <FileUploadInput
                    onChange={(files) => addFiles(files, 2)}
                    acceptedTypes="application/pdf, image/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                  <FileGridPro
                    files={client?.files_data?.filter((fd) => fd.order === 2)}
                    onDelete={() => removeFile(2)}
                    showDownload
                  />
                  <FormControl error={Boolean(filesDataValidationError(2))}>
                    <FloatingLabelInput
                      label="Nombre de la pestaña"
                      value={client?.files_data?.find((fd) => fd.order === 2)?.label ?? ''}
                      onChange={(e) => handleButtonLabel(2, e.target.value)}
                      slotProps={{
                        input: {
                          maxLength: 25,
                        },
                      }}
                    />
                    <FormHelperText>{filesDataValidationError(2) ?? ''}</FormHelperText>
                  </FormControl>
                </Stack>
              </OptionArrayBox>
            </Grid>
          ),
        },
        {
          title: 'Suscripción',
          content: (
            <Grid
              {...gridItemProps}
              md={12}
            >
              <OptionArrayBox
                label="Suscripción"
                sx={{ padding: 2 }}
                labelProps={{
                  endDecorator: (
                    <InfoTooltip
                      className="lh-base ps-2"
                      text={`Especificaciones técnicas:
                Formato: *.pdf *.jpg, *.docx, *.pptx, *.xlsx
                Peso máximo recomendado: 2MB`}
                    />
                  ),
                }}
                {...gridItemProps}
              >
                <Stack gap={2}>
                  <FileUploadInput
                    onChange={(files) => addFiles(files, 3)}
                    acceptedTypes="application/pdf, image/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  />
                  <FileGridPro
                    files={client?.files_data?.filter((fd) => fd.order === 3)}
                    onDelete={() => removeFile(3)}
                    showDownload
                  />
                </Stack>
              </OptionArrayBox>
            </Grid>
          ),
        },
        {
          title: 'Descripción',
          content: (
            <Grid
              {...gridItemProps}
              md={12}
            >
              <TextAreaInput<Client>
                label="Descripción"
                name="description"
                value={client?.description}
                onChange={setValue}
                minRows={6}
              />
            </Grid>
          ),
        },
      ],
    },
    {
      title: 'Estado Operativo en MediaCore',
      items: [
        {
          title: 'Configuración inicial',
          content: (
            <Grid {...gridContainerProps}>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <OptionArrayBox
                  sx={{ padding: 2 }}
                  label="Estado general"
                >
                  {gridSwitch({
                    label: 'Activo',
                    name: 'is_active',
                    disabled: !userLevel('CLIENT_ADMIN'),
                  })}
                </OptionArrayBox>
              </Grid>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <OptionArrayBox
                  sx={{ padding: 2 }}
                  label="Módulos"
                >
                  <Grid
                    container
                    xs={12}
                    spacing={2}
                  >
                    {queriedModules?.map((module) => (
                      <Grid
                        key={`module-${module.id}`}
                        xs={4}
                        md={3}
                      >
                        <GridSwitch
                          label={module.title}
                          name="modules"
                          disabled={!userLevel('CLIENT_ADMIN')}
                          checked={(client.modules as number[]).includes(module.id)}
                          setValue={() => handleModuleSelection(module.id)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </OptionArrayBox>
              </Grid>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <OptionArrayBox
                  sx={{ padding: 2 }}
                  label="Notificaciones"
                >
                  <Stack gap={2}>
                    {gridSwitch({
                      label: 'Activar notificaciones',
                      name: 'enable_notifications',
                      disabled: !userLevel('CLIENT_ADMIN'),
                    })}
                    <Grid
                      container
                      xs={12}
                      spacing={2}
                    >
                      {queriedModules?.map((module) => (
                        <Grid
                          key={`module-${module.id}`}
                          xs={4}
                          md={3}
                        >
                          <GridSwitch
                            label={module.title}
                            name="modules"
                            disabled={!userLevel('CLIENT_ADMIN')}
                            checked={(Array.isArray(client?.notifications_by_modules)
                              ? client.notifications_by_modules
                              : []
                            ).includes(module.id)}
                            setValue={() => handleModuleNotification(module.id)}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </OptionArrayBox>
              </Grid>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <OptionArrayBox
                  sx={{ padding: 2 }}
                  label="Opciones de MediaSite"
                >
                  <Stack gap={2}>
                    <Grid
                      container
                      xs={12}
                      spacing={2}
                    >
                      <Grid
                        xs={12}
                        md={6}
                      >
                        {gridSwitch({
                          label: 'Usar Compilador de sitios',
                          name: 'use_site_compiler',
                        })}
                        {gridSwitch({
                          label: 'Usar MediaPush v2',
                          name: 'uses_push_v2',
                        })}
                        {gridSwitch({
                          label: 'Usar Middleware v2',
                          name: 'uses_middleware_v2',
                        })}
                      </Grid>
                      <Grid
                        xs={12}
                        md={6}
                      >
                        {gridSwitch({
                          label: 'Mostrar propiedades retasadas en venta',
                          name: 'show_reassessment_sale',
                        })}
                        {gridSwitch({
                          label: 'Mostrar propiedades retasadas en alquiler',
                          name: 'show_reassessment_rent',
                        })}
                        {gridSwitch({
                          label: 'Mostrar propiedades retasadas en alquiler temporario',
                          name: 'show_reassessment_temporary_rent',
                        })}
                      </Grid>
                    </Grid>
                  </Stack>
                </OptionArrayBox>
              </Grid>
            </Grid>
          ),
        },
      ],
    },
    {
      title: 'Integraciones y Personalizaciones Técnicas',
      items: [
        {
          title: 'Iframe MediaMarketing',
          content: (
            <Grid {...gridContainerProps}>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <TextInput<Client>
                  label="Iframe MediaMarketing"
                  name="gantt_url"
                  value={client?.gantt_url}
                  onChange={setValue}
                  error={'gantt_url' in validationErrors}
                  errorMessage={validationErrors?.gantt_url}
                />
              </Grid>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <TextInput<Client>
                  label="Texto pestaña"
                  name="mediacontrol_label"
                  value={client?.mediacontrol_label}
                  onChange={setValue}
                />
              </Grid>
            </Grid>
          ),
          errorFields: [filesDataValidationError(1), filesDataValidationError(2)].filter(Boolean),
        },
        {
          title: 'Iframe MediaTracker',
          content: (
            <Grid {...gridContainerProps}>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <TextInput<Client>
                  label="Iframe MediaTracker"
                  name="iframe_mediatracker"
                  value={client?.iframe_mediatracker}
                  onChange={setValue}
                  error={'iframe_mediatracker' in validationErrors}
                  errorMessage={validationErrors?.iframe_mediatracker}
                />
              </Grid>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <TextInput<Client>
                  label="Texto pestaña"
                  name="mediatracker_label"
                  value={client?.mediatracker_label}
                  onChange={setValue}
                />
              </Grid>
            </Grid>
          ),
        },
        {
          title: 'UID MediaCore',
          content: (
            <Grid {...gridContainerProps}>
              <Grid
                {...gridItemProps}
                md={12}
              >
                <TextInput<Client>
                  label="UID MediaCore"
                  name="uid"
                  value={client?.uid ?? ''}
                  readOnly
                  endDecorator={
                    client?.uid ? (
                      <Tooltip
                        placement="top"
                        title="Copiado al portapapeles!"
                        enterDelay={250}
                        leaveDelay={250}
                        open={uidCopyTooltipOpen}
                        onClose={() => setUidCopyTooltipOpen(false)}
                        disableHoverListener
                        disableTouchListener
                      >
                        <IconButton
                          size="sm"
                          variant="plain"
                          onClick={handleCopyUid}
                          sx={{
                            minHeight: 'auto',
                          }}
                        >
                          <ContentCopy sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    ) : undefined
                  }
                />
              </Grid>
            </Grid>
          ),
        },
      ],
    },
  ]

  const steps: Step[] = [
    {
      title: !id ? 'Nuevo cliente' : 'Editar cliente',
      subtitle: `Ingresar datos del ${!id ? 'nuevo ' : ''}cliente`,
      content: (
        <TabContent
          tabs={[
            {
              title: 'Información',
              content: (
                <StepperAccordion
                  hasBeenVisualized={crossedSteps.includes(0) || Boolean(client?.id)}
                  sections={sections}
                  defaultExpanded={0}
                />
              ),
            },
            // {
            //   title: 'Actividad',
            //   hidden: !client?.id,
            //   content: client.id ? (
            //     <EventsCard
            //       id={client.id}
            //       allEvents={allClientEvents}
            //       isLoading={isBusyClientEvents}
            //       deleteEvent={deleteClientEvent}
            //       updateEvent={updateClientEvent}
            //       isDeletingEvent={isDeletingClientEvent}
            //       isUpdatingEvent={isUpdatingClientEvent}
            //       saveEvent={(_clientId, isMessageCreation) => saveClientEvent(isMessageCreation)}
            //       isSaving={isSavingEvent}
            //       onChange={(e) => {
            //         setEventValue<string>('content', e)
            //       }}
            //       content={clientEvent?.content ?? ''}
            //     />
            //   ) : (
            //     <></>
            //   ),
            // },
          ]}
        />
      ),
      completed: isValidSections(sections),
    },
  ]

  return (
    <>
      <Stepper
        error={isErrorClient && !isForbidden(errorClient)}
        onErrorReload={refetchClient}
        forbidden={isForbidden(errorClient)}
        isLoading={isLoadingClient}
        isSaving={isSaving}
        isSavingMessage={`Se está guardando el cliente "${client.name}"`}
        title={!id ? 'Nuevo cliente' : `Editar cliente: ${client.name}`}
        style="joy"
        steps={steps}
        onThroughStep={onThroughStep}
        buttons={[
          {
            label: 'Guardar',
            onClick: () =>
              saveClient()
                .then((savedClient) => {
                  const savedClientId = id ?? (savedClient as Client | undefined)?.id
                  if (!savedClientId) return undefined
                  return saveMarketingObjectives(savedClientId)
                })
                .then(() => onClose?.())
                .catch(() => {}),
            disabled: !isValidSteps(steps),
          },
        ]}
      />
      {sizeAlertModal}
    </>
  )
}

interface GridSwitchProps {
  label: string
  name: keyof Client
  disabled?: boolean
  checked?: boolean
  setValue: (name: keyof Client, value: boolean) => void
}

const GridSwitch = memo(
  ({ disabled, checked, setValue, name, label }: GridSwitchProps) => (
    <Typography
      level="body-md-light"
      startDecorator={
        <Switch
          size="sm"
          disabled={disabled}
          checked={Boolean(checked)}
          onChange={() => setValue(name, !checked)}
        />
      }
    >
      {label}
    </Typography>
  ),
  excludeFunctionsFromMemo,
)
